import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Linkedin, Twitter } from 'lucide-react';
import Logo from './Logo';
import { COMPANY, SOLUTIONS, LINKS } from '../config/siteContent';

/**
 * Footer - light theme.
 *
 * Five-column grid: Brand · Solutions · About · Resources · Connect (social).
 *
 * Footer links audited May 2026 - only includes destinations that ACTUALLY
 * resolve. Privacy Policy, Terms of Use, Status Page, Press, EDI Guide, etc.
 * were removed because we don't have those pages yet. Add them back when the
 * routes exist.
 */
const Footer = () => {
  return (
    <footer
      id="contact-footer"
      className="relative bg-bg-soft border-t border-border-subtle pt-16 pb-8"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Logo className="mb-4" height={40} />
            <p className="italic text-text-secondary text-sm leading-relaxed mb-6 max-w-sm">
              {COMPANY.tagline}
            </p>
            <div className="space-y-2.5 text-sm text-text-secondary">
              <div className="flex items-start gap-2.5">
                <MapPin size={16} className="mt-0.5 flex-shrink-0 text-accent-primary" />
                <div>
                  {COMPANY.address.street}
                  <br />
                  {COMPANY.address.poBox}
                  <br />
                  {COMPANY.address.city}, {COMPANY.address.state}{' '}
                  {COMPANY.address.zip}
                </div>
              </div>
              <a
                href={`tel:${COMPANY.phones.salesTollFree.replace(/\D/g, '')}`}
                className="flex items-center gap-2.5 hover:text-accent-primary transition-colors"
              >
                <Phone size={16} className="text-accent-primary" />
                Sales: {COMPANY.phones.salesTollFree}
              </a>
              <a
                href={`tel:${COMPANY.phones.supportTollFree.replace(/\D/g, '')}`}
                className="flex items-center gap-2.5 hover:text-accent-primary transition-colors"
              >
                <Phone size={16} className="text-accent-primary" />
                Support: {COMPANY.phones.supportTollFree}
              </a>
              <a
                href={`mailto:${COMPANY.emails.sales}`}
                className="flex items-center gap-2.5 hover:text-accent-primary transition-colors"
              >
                <Mail size={16} className="text-accent-primary" />
                {COMPANY.emails.sales}
              </a>
            </div>
          </div>

          {/* Solutions column - all 4 main + 2 extended map to real routes */}
          <FooterCol title="Solutions" className="lg:col-span-2">
            {SOLUTIONS.map((sol) => (
              <FooterRouterLink key={sol.id} to={`/solutions/${sol.id}`}>
                {sol.title}
              </FooterRouterLink>
            ))}
            <FooterRouterLink to="/solutions/hybrid-van">
              Hybrid VAN
            </FooterRouterLink>
            <FooterRouterLink to="/solutions/oftp2">OFTP2</FooterRouterLink>
          </FooterCol>

          {/* About column - all real routes */}
          <FooterCol title="About" className="lg:col-span-2">
            <FooterRouterLink to="/about">Company Profile</FooterRouterLink>
            <FooterRouterLink to="/about/management">Management</FooterRouterLink>
            <FooterRouterLink to="/about/careers">Careers</FooterRouterLink>
            <FooterRouterLink to="/case-studies">Case Studies</FooterRouterLink>
            <FooterRouterLink to="/compare">How We Compare</FooterRouterLink>
          </FooterCol>

          {/* Resources column - only real routes + external login.
             Removed Privacy Policy / Terms of Use / Documentation since those
             pages don't exist yet. Re-add when content is ready. */}
          <FooterCol title="Resources" className="lg:col-span-2">
            {/* Glossary hidden for now - uncomment to restore (also the nav item + route in App.js). */}
            {/* <FooterRouterLink to="/resources/glossary">Glossary</FooterRouterLink> */}
            <FooterRouterLink to="/resources/connections">
              Trading Partners
            </FooterRouterLink>
            <FooterRouterLink to="/contact">Contact Us</FooterRouterLink>
            <li>
              <a
                href={LINKS.iExchangeWebLogin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-accent-primary transition-colors"
              >
                iExchangeWeb Login ↗
              </a>
            </li>
          </FooterCol>

          {/* Connect column */}
          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-text-primary mb-4 text-sm uppercase tracking-wider">
              Connect
            </h4>
            <div className="flex gap-3">
              <a
                href={COMPANY.socialMedia.linkedin}
                aria-label="LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white border border-border-subtle flex items-center justify-center text-text-secondary hover:text-accent-primary hover:border-accent-primary/40 transition-all shadow-soft"
              >
                <Linkedin size={18} />
              </a>
              <a
                href={COMPANY.socialMedia.twitter}
                aria-label="Twitter"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white border border-border-subtle flex items-center justify-center text-text-secondary hover:text-accent-primary hover:border-accent-primary/40 transition-all shadow-soft"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-border-subtle pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-text-muted">
            © {new Date().getFullYear()} {COMPANY.fullName} All rights reserved.
          </div>
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <a
              href={(process.env.PUBLIC_URL || '') + '/pdf/PrivacyPolicy.pdf'}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-accent-primary transition-colors"
            >
              Privacy Policy
            </a>
            <span aria-hidden="true">·</span>
            <a
              href={(process.env.PUBLIC_URL || '') + '/pdf/TermOfUse.pdf'}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-accent-primary transition-colors"
            >
              Terms of Use
            </a>
            <span aria-hidden="true">·</span>
            <span>iExchangeWeb is a Trademark of {COMPANY.fullName}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, className = '', children }) => (
  <div className={className}>
    <h4 className="font-display font-bold text-text-primary mb-4 text-sm uppercase tracking-wider">
      {title}
    </h4>
    <ul className="space-y-2.5 text-sm">{children}</ul>
  </div>
);

const FooterRouterLink = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="text-text-secondary hover:text-accent-primary transition-colors"
    >
      {children}
    </Link>
  </li>
);

export default Footer;
